import Link from 'next/link';

import { generateServiceLogoUrlFromTheme } from '../../features/url/generateServiceLogoUrlFromTheme';

import styles from './ServiceLogo.module.scss';

import type { DefaultProps } from '../../types/DefaultProps';
import type { AppEnv } from '@/packages/nurse-senka-frontend-sdk';
import type { FC, HTMLAttributes } from 'react';

type Props = DefaultProps & {
  appEnv: AppEnv;
  width?: number;
  height?: number;
};

type ElementProps = Omit<HTMLAttributes<HTMLElement>, keyof Props>;

const defaultWidth = 126;
const defaultHeight = 31;

export const ServiceLogo: FC<Props & ElementProps> = ({
  appEnv,
  theme,
  width = defaultWidth,
  height = defaultHeight,
}) => (
  <Link href={generateServiceLogoUrlFromTheme(theme, appEnv)} prefetch={false}>
    <figure className={`${styles.wrapper}`}>
      <svg
        width={width}
        height={height}
        viewBox="0 0 126 31"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={theme?.logoTitle ? theme?.logoTitle : 'ナース専科'}
      >
        <title>{theme?.logoTitle ? theme?.logoTitle : 'ナース専科'}</title>
        <desc>国内最大級の看護師・看護学生コミュニティサイト</desc>
        <g clipPath="url(#clip0_16164_75954)">
          <path
            d="M20.745 8.58594H12.7114V2.07212C12.7114 1.84684 12.5274 1.65991 12.2949 1.65991H9.35073C9.12314 1.65991 8.93428 1.84205 8.93428 2.07212C8.93428 3.11701 8.93428 5.99287 8.93428 8.58594H0.416449C0.188855 8.58594 0 8.76808 0 8.99815V11.6056C0 11.8309 0.184012 12.0178 0.416449 12.0178H8.93912V13.3311C8.93912 14.5102 9.02144 17.8845 8.56626 19.4567C8.1256 20.9857 6.80845 24.801 1.93697 24.9927C1.71906 25.0023 1.54473 25.1844 1.54473 25.4049V28.3958C1.54473 28.6259 1.73359 28.8128 1.96603 28.808C9.45726 28.6019 11.8349 22.994 12.2513 20.516C12.6775 17.99 12.7114 14.1124 12.7114 13.3311V12.0178H20.745C20.9726 12.0178 21.1614 11.8357 21.1614 11.6056V8.99815C21.1614 8.77287 20.9774 8.58594 20.745 8.58594Z"
            fill="#5096FF"
          />
          <path
            d="M64.5641 23.7802L57.838 18.4838C62.3124 14.8938 62.3124 9.19956 62.3124 8.15467V7.74726C62.3124 4.71802 60.3802 4.71802 58.7338 4.71802H46.8892C46.6616 4.71802 46.4728 4.90016 46.4728 5.13022V7.74246C46.4728 7.96774 46.6568 8.15467 46.8892 8.15467H58.4481C58.4481 8.15467 58.7144 12.7225 55.2812 15.7421C52.6081 18.0956 49.1119 20.7461 45.0201 23.761C44.8457 23.8904 44.7392 24.0965 44.7392 24.3122V27.955C44.7392 28.2953 45.1363 28.4918 45.4075 28.2809C47.0297 27.0347 51.2862 23.7466 54.8841 20.8755L64.1525 28.1754C64.4237 28.3911 64.8256 28.1994 64.8256 27.8543V24.317C64.8256 24.1061 64.7287 23.9096 64.5641 23.7802Z"
            fill="#5096FF"
          />
          <path
            d="M107.807 12.986H110.766C110.993 12.986 111.182 12.8039 111.182 12.5738V9.96155C111.182 9.73628 110.998 9.54935 110.766 9.54935H107.347V5.13011C108.407 4.87608 109.507 4.55973 110.63 4.16191C110.795 4.10439 110.906 3.94622 110.906 3.77367V1.14225C110.906 0.849875 110.606 0.653358 110.335 0.758807C106.655 2.19194 102.95 2.68563 100.427 2.8438C100.209 2.85818 100.04 3.03553 100.04 3.25601V5.65735C100.04 5.89221 100.233 6.07915 100.471 6.06956C101.41 6.03601 102.514 5.95932 103.739 5.79635V9.54935H100.321C100.093 9.54935 99.9042 9.73149 99.9042 9.96155V12.5738C99.9042 12.7991 100.088 12.986 100.321 12.986H103.396C101.696 16.298 100.306 18.6994 98.8485 21.163C98.7275 21.3643 98.6936 21.604 98.7468 21.8293L99.5361 25.0646C99.6233 25.4241 100.112 25.496 100.301 25.1748C101.183 23.665 102.461 21.3979 103.739 19.0109V30.0878C103.739 30.313 103.923 30.5 104.156 30.5H106.931C107.158 30.5 107.347 30.3178 107.347 30.0878V17.5011C108.161 18.6514 109.1 19.8976 110.146 21.1295C110.369 21.3883 110.795 21.2924 110.872 20.9617L111.565 18.0667C111.633 17.7839 111.555 17.4867 111.361 17.271C110.064 15.8283 108.814 14.2897 107.812 12.986H107.807Z"
            fill="#5096FF"
          />
          <path
            d="M117.729 11.457H113.594C113.364 11.457 113.177 11.6416 113.177 11.8692V14.4767C113.177 14.7043 113.364 14.8889 113.594 14.8889H117.729C117.959 14.8889 118.146 14.7043 118.146 14.4767V11.8692C118.146 11.6416 117.959 11.457 117.729 11.457Z"
            fill="#5096FF"
          />
          <path
            d="M117.729 5.73413H113.594C113.364 5.73413 113.177 5.91868 113.177 6.14634V8.75378C113.177 8.98144 113.364 9.16599 113.594 9.16599H117.729C117.959 9.16599 118.146 8.98144 118.146 8.75378V6.14634C118.146 5.91868 117.959 5.73413 117.729 5.73413Z"
            fill="#5096FF"
          />
          <path
            d="M125.506 17.0745L123.395 17.4628V0.912206C123.395 0.686931 123.211 0.5 122.978 0.5H120.204C119.976 0.5 119.787 0.682138 119.787 0.912206V18.129L113.521 19.2842C113.322 19.3225 113.182 19.4903 113.182 19.6868V22.2415C113.182 22.5003 113.419 22.692 113.676 22.6441L119.792 21.5177V30.083C119.792 30.3083 119.976 30.4952 120.208 30.4952H122.983C123.211 30.4952 123.4 30.3083 123.4 30.083V20.8515L125.661 20.4345C125.86 20.3961 126 20.2284 126 20.0319V17.4772C126 17.2183 125.763 17.0266 125.506 17.0745Z"
            fill="#5096FF"
          />
          <path
            d="M78.5152 23.6746H74.5734C74.3434 23.6746 74.157 23.8591 74.157 24.0868V26.1478C74.157 26.3755 74.3434 26.56 74.5734 26.56H78.5152C78.7452 26.56 78.9316 26.3755 78.9316 26.1478V24.0868C78.9316 23.8591 78.7452 23.6746 78.5152 23.6746Z"
            fill="#5096FF"
          />
          <path
            d="M72.3749 17.8175C72.3749 18.0475 72.559 18.2297 72.7914 18.2297H84.9992V19.5094H69.5566C69.3291 19.5094 69.1402 19.6916 69.1402 19.9216V21.9827C69.1402 22.2079 69.3242 22.3949 69.5566 22.3949H84.9992V25.5583C84.9992 26.2965 84.7425 27.0394 83.1154 27.0394H80.0453C79.7645 27.0394 79.5563 27.303 79.6241 27.5762L80.2681 30.1789C80.3165 30.3658 80.486 30.5 80.6845 30.5H83.9193C88.4373 30.5 88.6068 27.4708 88.6068 26.1335V22.3949H94.8632C95.0908 22.3949 95.2796 22.2127 95.2796 21.9827V19.9216C95.2796 19.6964 95.0956 19.5094 94.8632 19.5094H88.6068V18.2297H91.6236C91.8512 18.2297 92.04 18.0475 92.04 17.8175V7.53147C92.04 7.3062 91.856 7.11927 91.6236 7.11927H84.0113V5.83951H93.241C93.4686 5.83951 93.6574 5.65737 93.6574 5.4273V3.36627C93.6574 3.141 93.4734 2.95407 93.241 2.95407H84.0113V0.912206C84.0113 0.686931 83.8273 0.5 83.5948 0.5H80.8201C80.5925 0.5 80.4037 0.682138 80.4037 0.912206V2.95886H71.174C70.9464 2.95886 70.7576 3.141 70.7576 3.37107V5.4321C70.7576 5.65737 70.9416 5.8443 71.174 5.8443H80.4037V7.12406H72.7914C72.5638 7.12406 72.3749 7.3062 72.3749 7.53627V17.8223V17.8175ZM88.4276 15.8283H84.0065V13.8152H88.4276V15.8283ZM88.4276 9.5254V11.4091H84.0065V9.5254H88.4276ZM75.9826 9.5254H80.4037V11.4091H75.9826V9.5254ZM80.3988 13.8152V15.8283H75.9777V13.8152H80.3988Z"
            fill="#5096FF"
          />
          <path
            d="M40.4149 13.5947H25.4131C25.1831 13.5947 24.9966 13.7793 24.9966 14.0069V16.9882C24.9966 17.2159 25.1831 17.4004 25.4131 17.4004H40.4149C40.6449 17.4004 40.8314 17.2159 40.8314 16.9882V14.0069C40.8314 13.7793 40.6449 13.5947 40.4149 13.5947Z"
            fill="#5096FF"
          />
        </g>
        <defs>
          <clipPath id="clip0_16164_75954">
            <rect
              width="126"
              height="30"
              fill="white"
              transform="translate(0 0.5)"
            />
          </clipPath>
        </defs>
      </svg>
    </figure>
  </Link>
);
